import { useSigma } from "@react-sigma/core";
import { circular } from "graphology-layout";
import { FC, useRef } from "react";
import { animateNodes } from "sigma/utils";
import { PlainObject } from "sigma/types";
import {
  MdAutoGraph,
  MdShuffle,
  MdRadioButtonChecked,
  MdScatterPlot,
} from "react-icons/md";

import Panel from "./Panel";

const LayoutPanel: FC = () => {
  const sigma = useSigma();
  const graph = sigma.getGraph();
  const cancelAnimationRef = useRef<(() => void) | null>(null);

  const randomLayout = () => {
    if (cancelAnimationRef.current) {
      cancelAnimationRef.current();
    }

    // Use a fixed square area for random layout
    const areaSize = 2000; // Size of the square area

    const randomPositions: PlainObject<PlainObject<number>> = {};
    graph.forEachNode((node) => {
      randomPositions[node] = {
        x: (Math.random() - 0.5) * areaSize,
        y: (Math.random() - 0.5) * areaSize,
      };
    });

    cancelAnimationRef.current = animateNodes(graph, randomPositions, {
      duration: 2000,
    });
  };

  const circularLayout = () => {
    if (cancelAnimationRef.current) {
      cancelAnimationRef.current();
    }

    const circularPositions = circular(graph, { scale: 100 });
    cancelAnimationRef.current = animateNodes(graph, circularPositions, {
      duration: 2000,
      easing: "linear",
    });
  };

  const splatterLayout = () => {
    if (cancelAnimationRef.current) {
      cancelAnimationRef.current();
    }

    const defaultPositions: PlainObject<PlainObject<number>> = {};
    graph.forEachNode((node) => {
      defaultPositions[node] = {
        x: graph.getNodeAttribute(node, "original_x"),
        y: graph.getNodeAttribute(node, "original_y"),
      };
    });

    // Animate nodes to their original positions
    cancelAnimationRef.current = animateNodes(graph, defaultPositions, {
      duration: 2000,
      easing: "cubicInOut", // Smooth easing for a more polished animation
    });
  };

  return (
    <Panel
      title={
        <>
          <MdAutoGraph className="text-muted" /> Layout
        </>
      }
    >
      <div className="layout-controls">
        <button
          type="button"
          className="layout-button"
          onClick={circularLayout}
          title="Circular Layout"
        >
          <MdRadioButtonChecked />
          <span>Circular</span>
        </button>
        <button
          type="button"
          className="layout-button"
          onClick={splatterLayout}
          title="Splatter View"
        >
          <MdScatterPlot />
          <span>Splatter View</span>
        </button>
        <button
          type="button"
          className="layout-button"
          onClick={randomLayout}
          title="Random Layout"
        >
          <MdShuffle />
          <span>Random</span>
        </button>
      </div>
    </Panel>
  );
};

export default LayoutPanel;
